import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { Button } from '../components/Button'
import { DownloadCard } from '../components/DownloadCard'
import { EmptyState } from '../components/EmptyState'
import { ReviewAcceptModal } from '../components/ReviewAcceptModal'
import { SkeletonList } from '../components/Skeleton'
import { InboxIcon } from '../components/icons'
import { getDb } from '../lib/db'
import {
  buildDownloadCards,
  recordAcceptance,
  requestDownload,
  type DownloadCardModel,
  type DownloadPhase,
} from '../lib/download-flow'
import type {
  AcceptanceWithVersion,
  AgreementWithVersion,
  DeliverableVersionPublicRow,
  EntitlementWithProduct,
} from '../lib/types'

/**
 * Downloads: entitlement cards with the gated download button state machine
 * (locked → reviewing → ready → downloading → done). Every click goes through
 * the server-side gate (functions/api/download/[versionId]) which re-checks
 * session → entitlement → acceptance and returns a short-lived signed URL.
 */
export function DownloadsPage() {
  const { user, session, memberships } = useAuth()
  const [entitlements, setEntitlements] = useState<EntitlementWithProduct[]>([])
  const [versions, setVersions] = useState<DeliverableVersionPublicRow[]>([])
  const [agreements, setAgreements] = useState<AgreementWithVersion[]>([])
  const [acceptances, setAcceptances] = useState<AcceptanceWithVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [runtimePhases, setRuntimePhases] = useState<Record<string, DownloadPhase>>({})
  const [notices, setNotices] = useState<Record<string, string | null>>({})
  const [reviewCard, setReviewCard] = useState<DownloadCardModel | null>(null)
  const [accepting, setAccepting] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)
    try {
      const [entRows, agreementRows, acceptanceRows] = await Promise.all([
        getDb().listMyEntitlements(user.id, memberships.map((m) => m.organization_id)),
        getDb().listCurrentAgreements(),
        getDb().listMyAcceptances(user.id),
      ])
      const versionRows = await getDb().listCurrentDeliverableVersions(
        entRows.flatMap((row) => (row.products ? [row.products.id] : [])),
      )
      setEntitlements(entRows)
      setAgreements(agreementRows)
      setAcceptances(acceptanceRows)
      setVersions(versionRows)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load your downloads')
    } finally {
      setLoading(false)
    }
  }, [user, memberships])

  useEffect(() => {
    void load()
  }, [load])

  const cards = useMemo(
    () =>
      buildDownloadCards({
        entitlements,
        versions,
        agreements,
        acceptances,
      }),
    [entitlements, versions, agreements, acceptances],
  )

  const phaseFor = (card: DownloadCardModel): DownloadPhase =>
    runtimePhases[card.key] ?? card.restingPhase

  const handleDownload = async (card: DownloadCardModel) => {
    if (!card.versionId || !session) return
    setRuntimePhases((prev) => ({ ...prev, [card.key]: 'downloading' }))
    setNotices((prev) => ({ ...prev, [card.key]: null }))

    const result = await requestDownload(card.versionId, session.access_token)
    if (result.ok) {
      setRuntimePhases((prev) => ({ ...prev, [card.key]: 'done' }))
      // Follow the short-lived signed URL — the bytes come from Storage.
      window.location.assign(result.url)
      return
    }

    setRuntimePhases((prev) => {
      const next = { ...prev }
      delete next[card.key]
      return next
    })

    if (result.reason === 'acceptance_required' || result.reason === 'acceptance_stale') {
      if (card.requiredDocument) {
        setModalError(null)
        setReviewCard(card)
      } else {
        setNotices((prev) => ({ ...prev, [card.key]: 'The required license document is not available yet.' }))
      }
      return
    }
    if (result.reason === 'no_entitlement' || result.reason === 'no_session') {
      setNotices((prev) => ({ ...prev, [card.key]: 'Your entitlement is not active for this product.' }))
      return
    }
    setNotices((prev) => ({ ...prev, [card.key]: 'Download failed — please try again.' }))
  }

  const handleAccept = async () => {
    if (!reviewCard?.requiredDocument || !user) return
    setAccepting(true)
    setModalError(null)
    try {
      await recordAcceptance({
        userId: user.id,
        documentVersionId: reviewCard.requiredDocument.currentVersionId,
      })
      setReviewCard(null)
      setRuntimePhases({})
      await load()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to record your acceptance')
    } finally {
      setAccepting(false)
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-3">
        <h1 className="font-display text-display-l text-ink">Downloads</h1>
        <p className="max-w-prose text-body-l text-ink-muted">
          Everything delivered to your workspace — templates, builds and licenses tied to your
          entitlements. Downloads are gated on your entitlements and the current delivery license.
        </p>
      </header>

      {loading && <SkeletonList rows={3} label="Loading your downloads" />}

      {!loading && error && (
        <div className="flex flex-col items-start gap-4 rounded-2xl border border-terracotta-deep/30 bg-terracotta-deep/5 p-6">
          <p className="text-body text-terracotta-deep">{error}</p>
          <Button variant="secondary" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      )}

      {!loading && !error && cards.length === 0 && (
        <EmptyState
          icon={<InboxIcon />}
          title="Nothing here yet"
          description="When you purchase or are granted a product, your downloads will show up here. Entitlements are granted automatically at checkout or by the nMach team."
        />
      )}

      {!loading && !error && cards.length > 0 && (
        <ul className="flex flex-col gap-3">
          {cards.map((card) => (
            <DownloadCard
              key={card.key}
              title={card.title}
              subtitle={card.subtitle}
              statusTone={card.statusTone}
              statusLabel={card.statusLabel}
              phase={phaseFor(card)}
              notice={notices[card.key] ?? card.notice}
              onReview={() => {
                setModalError(null)
                setReviewCard(card)
              }}
              onDownload={() => void handleDownload(card)}
            />
          ))}
        </ul>
      )}

      {!loading && cards.length > 0 && (
        <p className="text-small text-ink-faint">
          Download links are signed and expire within 5 minutes. Every download is recorded with
          the accepted license version that unlocked it.
        </p>
      )}

      <ReviewAcceptModal
        open={reviewCard !== null}
        document={
          reviewCard?.requiredDocument
            ? {
                title: reviewCard.requiredDocument.title,
                version: reviewCard.requiredDocument.version,
                summary: reviewCard.requiredDocument.summary,
                rendered_text: reviewCard.requiredDocument.rendered_text,
              }
            : null
        }
        mode="accept"
        accepting={accepting}
        error={modalError}
        onAccept={() => void handleAccept()}
        onClose={() => setReviewCard(null)}
      />
    </div>
  )
}
